import { ApiRoutingUtils } from '@/Routing';
import { AddressType } from '@/Enums';
import { rest } from '../Rest';
import { GenerateLabelsResponse } from './Responses/GenerateLabelsResponse';
import { GenerateLabelsRequest } from './Requests/GenerateLabelsRequest';
import { PrepareLabelsRequest } from './Requests/PrepareLabelsRequest';
import { GetPackagesForOrderRequest } from './Requests/GetPackagesForOrderRequest';
import { GetPackagesForOrderResponse } from './Responses/GetPackagesForOrderResponse';
import { GetPacklinkProductsRequest } from './Requests/GetPacklinkProductsRequest';
import { GetPacklinkProductsResponse } from './Responses/GetPacklinkProductsResponse';
import { GetDetailedLogisticInfoRequest } from './Requests/GetDetailedLogisticInfoRequest';
import { GetDetailedLogisticInfoResponse } from './Responses/GetDetailedLogisticInfoResponse';
import { GetLogisticsByWarehouseRequest } from './Requests/GetLogisticsByWarehouseRequest';
import { GetLogisticsByWarehouseResponse } from './Responses/GetLogisticsByWarehouseResponse';
import { GetLogisticCountriesResponse } from './Responses/GetLogisticCountriesResponse';
import { GetLogisticCountriesRequest } from './Requests/GetLogisticCountriesRequest';
import { OrderAddressValidationRequest } from './Requests/OrderAddressValidationRequest';
import { OrderAddressValidationResponse } from './Responses/OrderAddressValidationResponse';
import { ApplyAddressChangesRequest } from './Requests/ApplyAddressChangesRequest';
import { ApplyAddressChangesResponse } from './Responses/ApplyAddressChangesResponse';
import { LabelPrintedStatusResponse } from './Responses/LabelPrintedStatusResponse';
import { GetAvailableLogisticsRequest } from '@/Api/Logistics/Requests/GetAvailableLogisticsRequest';
import { GetAvailableLogisticsResponse } from '@/Api/Logistics/Responses/GetAvailableLogisticsResponse';

const url = ApiRoutingUtils.createUrl('logistics');

async function validateOrdersAddresses(
  request: OrderAddressValidationRequest,
): Promise<OrderAddressValidationResponse> {
  return rest.post(`${url}/validate-address`, request);
}

async function applyAddressChanges(request: ApplyAddressChangesRequest): Promise<ApplyAddressChangesResponse> {
  return rest.put(`${url}/apply-address`, request);
}

async function getFormattedAddress(orderId: number, addressType: AddressType): Promise<string> {
  return rest.get(`${url}/formatted-address`, { addressType, orderId });
}

async function prepareLabels(request: PrepareLabelsRequest): Promise<void> {
  return rest.post(`${url}/prepare-labels`, request);
}

async function generateLabels(request: GenerateLabelsRequest): Promise<GenerateLabelsResponse> {
  return rest.get(`${url}/generate-labels`, request);
}

async function setLabelPrintedStatus(orderLabelIds: number[]): Promise<LabelPrintedStatusResponse> {
  return rest.put(`${url}/printed-label`, { orderLabelIds: orderLabelIds });
}

async function getPackagesForOrders(request: GetPackagesForOrderRequest): Promise<GetPackagesForOrderResponse> {
  return rest.get(`${url}/get-packages-for-order`, request);
}

async function getLogisticProducts(request: GetPacklinkProductsRequest): Promise<GetPacklinkProductsResponse> {
  return rest.post(`${url}/get-logistic-products`, request);
}

async function getLogisticsByWarehouse(
  request: GetLogisticsByWarehouseRequest,
): Promise<GetLogisticsByWarehouseResponse> {
  return rest.get(`${url}/logistics-by-warehouse`, request);
}

async function getDetailedLogisticInfo(
  request: GetDetailedLogisticInfoRequest,
): Promise<GetDetailedLogisticInfoResponse> {
  return rest.get(`${url}/logistic-info`, request);
}

async function getLogisticCountries(request: GetLogisticCountriesRequest): Promise<GetLogisticCountriesResponse> {
  return rest.get(`${url}/logistic-countries`, request);
}

async function getAvailableLogistics(request: GetAvailableLogisticsRequest): Promise<GetAvailableLogisticsResponse> {
  return rest.get(`${url}/available-logistics`, request);
}

export const LogisticsApi = {
  validateOrdersAddresses,
  applyAddressChanges,
  getFormattedAddress,
  prepareLabels,
  generateLabels,
  setLabelPrintedStatus,
  getPackagesForOrders,
  getLogisticProducts,
  getDetailedLogisticInfo,
  getLogisticsByWarehouse,
  getLogisticCountries,
  getAvailableLogistics,
};
